import { getStaffingColor, getTurnoverStatus } from "./utils";
import { buildDismissalsByDepartmentId } from "./employeeListUtils";

const TENURE_DISPLAY_KEYS = [
  "0_0_6_months",
  "0_6_1_year",
  "1_3_years",
  "3_5_years",
  "5_10_years",
  "10_and_more",
];

const TENURE_SOURCE_KEYS = {
  "0_0_6_months": ["0_0_6_months"],
  "0_6_1_year": ["0_6_1_year"],
  "1_3_years": ["1_3_years"],
  "3_5_years": ["3_5_years"],
  "5_10_years": ["5_10_years"],
  "10_and_more": ["10_15_years", "15_20_years", "over_20_years"],
};

const TENURE_DISPLAY_LABELS = {
  "0_0_6_months": "0-6 мес.",
  "0_6_1_year": "6 мес. - 1 год",
  "1_3_years": "1-3 года",
  "3_5_years": "3-5 лет",
  "5_10_years": "5-10 лет",
  "10_and_more": "10 и более",
};

const TENURE_KEY_COLORS = {
  "0_0_6_months": "#ff7c7c",
  "0_6_1_year": "#ffa74f",
  "1_3_years": "#ffd646",
  "3_5_years": "#38d97b",
  "5_10_years": "#30d5c2",
  "10_and_more": "#5ab0ff",
};

const getTenureCountByDisplayKey = (items, displayKey) => {
  const sourceKeys = TENURE_SOURCE_KEYS[displayKey] || [displayKey];
  return sourceKeys.reduce((sum, key) => {
    const item = (items || []).find((entry) => entry.key === key);
    return sum + (item?.count || 0);
  }, 0);
};

const getTenureEmployeesByDisplayKey = (items, displayKey) => {
  const sourceKeys = TENURE_SOURCE_KEYS[displayKey] || [displayKey];
  return sourceKeys.flatMap((key) => {
    const item = (items || []).find((entry) => entry.key === key);
    return Array.isArray(item?.employees) ? item.employees.filter(Boolean) : [];
  });
};

const DEPARTMENT_SHORT_CODES = {
  "Отделение Эскизного Проектирования": "ЭП",
  "РП ОЗ": "ОЗ",
  "РП МЖК": "МЖК",
  "АН и качество": "АН",
  "Административное отделение": "АУП",
  "Отделение построения": "Построение",
  "Финансовое отделение": "Финансы",
  "Отделение квалификации": "Квалификация",
  Производство: "Производство",
};

const extractDepartmentCode = (name) => {
  const numbered = name.match(/^\d+(?:\.\d+)?\s+(.+)$/);
  const label = numbered ? numbered[1] : name;
  return DEPARTMENT_SHORT_CODES[label] || label;
};

const getCountByKeys = (items, keys) =>
  items
    .filter((item) => keys.includes(item.key))
    .reduce((sum, item) => sum + item.count, 0);

const getStaffingBadge = (percent) => {
  if (percent == null) return null;
  const color = getStaffingColor(percent);
  if (color === "green") return { text: "Норма", tone: "green" };
  if (color === "orange") return { text: "Внимание", tone: "orange" };
  return { text: "Риск", tone: "red" };
};

const normalizePlan = (plan) =>
  plan != null && plan > 0 ? plan : null;

const buildTenureCategories = () =>
  TENURE_DISPLAY_KEYS.map((key) => ({
    key,
    label: TENURE_DISPLAY_LABELS[key],
    color: TENURE_KEY_COLORS[key],
  }));

const mapTenureDepartment = (dept) => {
  const allItems = dept.tenure_distribution?.items || [];
  const total = dept.tenure_distribution?.total || 0;

  const breakdown = Object.fromEntries(
    TENURE_DISPLAY_KEYS.map((key) => [key, getTenureCountByDisplayKey(allItems, key)]),
  );

  const tenureEmployees = Object.fromEntries(
    TENURE_DISPLAY_KEYS.map((key) => [
      key,
      getTenureEmployeesByDisplayKey(allItems, key),
    ]),
  );

  return {
    id: dept.department_id,
    code: extractDepartmentCode(dept.department_name),
    name: dept.department_name,
    total,
    employees: Array.isArray(dept.employees) ? dept.employees.filter(Boolean) : [],
    breakdown,
    tenureEmployees,
  };
};

const mapGenderDepartment = (dept) => {
  const distribution = dept.gender_distribution || {};
  const malePercent = distribution.male_percent ?? 0;
  const femalePercent = distribution.female_percent ?? 0;
  const unknownCount =
    distribution.items?.find((item) => item.key === "unknown")?.count || 0;
  const total = distribution.total || dept.employee_count || 0;
  const unknownPercent =
    total > 0
      ? Math.max(0, Math.round((unknownCount / total) * 1000) / 10)
      : Math.max(0, Math.round((100 - malePercent - femalePercent) * 10) / 10);

  return {
    id: dept.department_id,
    code: extractDepartmentCode(dept.department_name),
    name: dept.department_name,
    womenPercent: femalePercent,
    menPercent: malePercent,
    unknownPercent,
  };
};

const mapTurnoverDepartment = (dept, dismissalsByDepartmentId) => {
  const turnover = dept.turnover || {};
  const turnoverPercent = turnover.percent ?? 0;
  const status = getTurnoverStatus(turnoverPercent);

  return {
    id: dept.department_id,
    code: extractDepartmentCode(dept.department_name),
    name: dept.department_name,
    resigned: turnover.dismissed_count ?? 0,
    dismissedEmployees: dismissalsByDepartmentId.get(dept.department_id) || [],
    turnoverPercent,
    status,
    statusLabel: status === "risk" ? "Риск" : "Норма",
  };
};

const aggregateGender = (departments, actualHeadcount) => {
  const totals = { male: 0, female: 0, unknown: 0 };

  departments.forEach((dept) => {
    (dept.gender_distribution?.items || []).forEach((item) => {
      if (item.key === "male") totals.male += item.count;
      if (item.key === "female") totals.female += item.count;
      if (item.key === "unknown") totals.unknown += item.count;
    });
  });

  const summedTotal = totals.male + totals.female + totals.unknown;
  // Unique headcount is authoritative; department sums can double-count
  // people assigned to multiple departments.
  const total =
    actualHeadcount != null && actualHeadcount > 0
      ? actualHeadcount
      : summedTotal;

  let male = totals.male;
  let female = totals.female;
  let unknown = totals.unknown;

  if (summedTotal > 0 && total !== summedTotal) {
    male = Math.round((totals.male / summedTotal) * total);
    female = Math.round((totals.female / summedTotal) * total);
    unknown = Math.max(0, total - male - female);
  }

  return {
    womenPercent: total > 0 ? Math.round((female / total) * 1000) / 10 : 0,
    menPercent: total > 0 ? Math.round((male / total) * 1000) / 10 : 0,
    unknownPercent:
      total > 0 ? Math.round((unknown / total) * 1000) / 10 : 0,
    total,
    items: [
      male > 0 && {
        key: "male",
        label: "мужчины",
        count: male,
        percent: total > 0 ? Math.round((male / total) * 1000) / 10 : 0,
      },
      female > 0 && {
        key: "female",
        label: "женщины",
        count: female,
        percent: total > 0 ? Math.round((female / total) * 1000) / 10 : 0,
      },
      unknown > 0 && {
        key: "unknown",
        label: "не указан",
        count: unknown,
        percent: total > 0 ? Math.round((unknown / total) * 1000) / 10 : 0,
      },
    ].filter(Boolean),
  };
};

const aggregateTenureHighlights = (departments) => {
  const items = departments.flatMap(
    (dept) => dept.tenure_distribution?.items || [],
  );
  const total = departments.reduce(
    (sum, dept) => sum + (dept.tenure_distribution?.total || 0),
    0,
  );

  if (!total) return [];

  return [
    {
      label: "Новички до 1 года",
      percent: Math.round(
        (getCountByKeys(items, ["0_0_6_months", "0_6_1_year"]) / total) * 100,
      ),
      tone: "pink",
    },
    {
      label: "Ядро 3–10 лет",
      percent: Math.round(
        (getCountByKeys(items, ["3_5_years", "5_10_years"]) / total) * 100,
      ),
      tone: "green",
    },
    {
      label: "Эксперты 10+ лет",
      percent: Math.round(
        (getCountByKeys(items, [
          "10_15_years",
          "15_20_years",
          "over_20_years",
        ]) /
          total) *
          100,
      ),
      tone: "blue",
    },
  ];
};

const mapDepartment = (dept) => {
  const plan = normalizePlan(dept.plan);
  const fact = dept.employee_count ?? dept.fact ?? 0;
  const deptStaffingPercent =
    dept.staffing_adequacy_percent != null
      ? Math.round(dept.staffing_adequacy_percent)
      : plan
        ? Math.round((fact / plan) * 100)
        : null;

  return {
    id: dept.department_id ?? dept.id,
    code: extractDepartmentCode(dept.department_name ?? dept.name),
    name: dept.department_name ?? dept.name,
    plan,
    fact,
    deficit: plan != null ? fact - plan : null,
    staffingPercent: deptStaffingPercent,
    employees: Array.isArray(dept.employees) ? dept.employees.filter(Boolean) : [],
  };
};

const recalculateSummary = (departments, currentSummary) => {
  const withPlan = departments.filter((dept) => dept.plan != null);
  const plannedHeadcount = withPlan.length
    ? withPlan.reduce((sum, dept) => sum + dept.plan, 0)
    : null;
  const actualHeadcount = currentSummary.actualHeadcount;
  const staffingPercent =
    plannedHeadcount != null && plannedHeadcount > 0
      ? Math.round((actualHeadcount / plannedHeadcount) * 100)
      : null;
  const deficit =
    plannedHeadcount != null ? actualHeadcount - plannedHeadcount : null;

  return {
    ...currentSummary,
    plannedHeadcount:
      plannedHeadcount != null && plannedHeadcount > 0 ? plannedHeadcount : null,
    staffingPercent,
    deficit,
    staffingBadge: getStaffingBadge(staffingPercent),
    deficitBadge:
      deficit != null && deficit < 0
        ? { text: "Риск", tone: "red" }
        : null,
  };
};

const getActiveDepartments = (departments) =>
  (departments || []).filter((dept) => (dept.employee_count || 0) > 0);

export const mapStaffStatsToDashboard = (data) => {
  const rawDepartments = data.departments || [];
  const activeDepartments = getActiveDepartments(rawDepartments);
  const plannedHeadcount = normalizePlan(data.staffing_adequacy?.planned_headcount);
  const actualHeadcount =
    data.staffing_adequacy?.actual_headcount ?? data.active_users_count ?? 0;
  const staffingPercent =
    data.staffing_adequacy?.percent != null
      ? Math.round(data.staffing_adequacy.percent)
      : plannedHeadcount
        ? Math.round((actualHeadcount / plannedHeadcount) * 100)
        : null;
  const deficit =
    plannedHeadcount != null ? actualHeadcount - plannedHeadcount : null;
  const turnoverPercent = data.turnover_rate?.percent ?? 0;

  const summary = {
    plannedHeadcount,
    actualHeadcount,
    staffingPercent,
    deficit,
    turnoverPercent,
    turnoverAboveNorm: null,
    staffingBadge: getStaffingBadge(staffingPercent),
    deficitBadge:
      deficit != null && deficit < 0
        ? { text: "Риск", tone: "red" }
        : null,
  };

  const staffingByDepartment = rawDepartments.map(mapDepartment);
  const tenureCategories = buildTenureCategories();
  const aggregatedGender = aggregateGender(activeDepartments, actualHeadcount);

  const dismissalsByDepartmentId = buildDismissalsByDepartmentId(data.dismissals);

  const turnoverItems = activeDepartments
    .map((dept) => mapTurnoverDepartment(dept, dismissalsByDepartmentId))
    .filter((item) => item.turnoverPercent != null)
    .sort((a, b) => b.turnoverPercent - a.turnoverPercent);

  return {
    summary,
    staffingByDepartment,
    turnover: {
      summary: {
        dismissedCount: data.turnover_rate?.dismissed_count ?? 0,
        averageHeadcount: data.turnover_rate?.average_headcount ?? 0,
        turnoverPercent,
        hiresCount: data.hires?.count ?? 0,
        transfersCount: data.transfers?.count ?? 0,
      },
      items: turnoverItems,
      dismissals: data.dismissals?.items || [],
      hires: data.hires?.items || [],
      transfers: data.transfers?.items || [],
    },
    tenure: {
      highlights: aggregateTenureHighlights(activeDepartments),
      categories: tenureCategories,
      departments: activeDepartments
        .map(mapTenureDepartment)
        .filter((dept) => dept.total > 0),
    },
    gender: {
      ...aggregatedGender,
      hasDepartmentBreakdown: true,
      departments: activeDepartments
        .map(mapGenderDepartment)
        .filter(
          (dept) =>
            dept.womenPercent != null ||
            dept.menPercent != null ||
            dept.unknownPercent > 0,
        ),
    },
    period: data.period,
  };
};

export const applyHeadcountPlansUpdate = (dashboardData, updatedDepartments) => {
  const updatedMap = new Map(
    updatedDepartments.map((dept) => [dept.department_id, mapDepartment(dept)]),
  );

  const staffingByDepartment = dashboardData.staffingByDepartment.map((dept) =>
    updatedMap.has(dept.id) ? updatedMap.get(dept.id) : dept,
  );

  return {
    ...dashboardData,
    staffingByDepartment,
    summary: recalculateSummary(staffingByDepartment, dashboardData.summary),
  };
};

export const getStaffingTotals = (departments, summary) => {
  const withPlan = departments.filter((dept) => dept.plan != null && dept.plan > 0);
  const plan = withPlan.reduce((sum, dept) => sum + dept.plan, 0);
  // Use unique active headcount — summing department facts double-counts
  // employees assigned to multiple departments.
  const fact = summary.actualHeadcount ?? 0;
  const departmentFactSum = departments.reduce(
    (sum, dept) => sum + (dept.fact || 0),
    0,
  );
  const hasDepartmentOverlap = departmentFactSum !== fact;

  if (withPlan.length > 0) {
    return {
      plan,
      fact,
      deficit: fact - plan,
      staffingPercent: plan > 0 ? Math.round((fact / plan) * 100) : null,
      hasPlan: true,
      departmentFactSum,
      hasDepartmentOverlap,
    };
  }

  return {
    plan: summary.plannedHeadcount,
    fact,
    deficit: summary.deficit,
    staffingPercent: summary.staffingPercent,
    hasPlan: summary.plannedHeadcount != null,
    departmentFactSum,
    hasDepartmentOverlap,
  };
};
